/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {
  makeStyles,
  withStyles
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import {translate} from '@ion/core/src/locale/locale';
import {
  EventCodeValueType,
  getEventName
} from '@ion/core/src/services/EventCodes';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {useMedia} from '../../hooks/useMedia';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor, dlsShadow, dlsTypography, dlsZIndex} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme, IconSize} from '../../types/types';
import {getFormattedDate} from '../../utils/ComponentUtils';
import CloseButton from '../buttons/CloseButton';
import {PrimaryButton} from '../buttons/PrimaryButton';
import VideoPlayer from '../videoplayer/VideoPlayer';

export interface IonVisionDialogProps {
  videoOpen: boolean;
  setVideoOpen: Function;
  urls: string[];
  showVideoPlayer?: boolean;
  eventData?: any;
  elementId: AnalyticsIds;
  selectedAsset?: CVSAssetType;
  renderPlayerOnly?: boolean;
  audio?: boolean;
}

export type DialogStyles = {
  elementWidth?: number;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  ionVisionDialogRoot : {
    zIndex : `${dlsZIndex('ionVisionDialogRoot')} !important` as any
  },
  paper : {
    [theme.breakpoints.down('sm')] : {
      margin : 0,
      width  : 'auto'
    }
  },
  scrollPaper : {
    [theme.breakpoints.down('sm')] : {
      alignItems : 'end'
    }
  },
  paperMob : {
    marginTop : (props: { isPhone: boolean }) => props.isPhone ? '0px' : '15px',
    width     : '100%',
    maxWidth  : '100%',
    maxHeight : '100%',
    overflow  : 'hidden'
  },
  fullScreenPaper : {
    backgroundColor : 'transparent !important'
  },
  closeButton : {
    display    : 'flex',
    alignItems : 'center',
    '&:hover'  : {
      cursor : 'pointer'
    }
  },
  titleContent : {
    margin : 'auto'
  },
  descHide : {
    opacity : '0'
  },
  controlsRight : {
    minHeight    : '64px',
    maxHeight    : '64px',
    display      : 'flex',
    alignItems   : 'center',
    textAlign    : 'right',
    paddingRight : '64px',
    paddingLeft  : '24px',
    borderBottom : toggleThemeStyle(`solid 1px ${dlsColor('grey4')}`, `solid 1px ${dlsColor('pastelTeal')}`)
  },
  titleStyle : ({isCurrentTheme}: { isCurrentTheme: boolean }) => isCurrentTheme ? {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontStyle     : 'normal',
    fontSize      : '16px',
    lineHeight    : '20px',
    letterSpacing : '0px',
    verticalAlign : 'middle'
  } : {
    fontSize   : '20px',
    lineHeight : '30px'
  },
  bodyStyle : {
    ...dlsTypography('body1'),
    textAlign : 'center !important'
  },
  dialogbg : {
    backgroundColor : toggleThemeStyle(dlsColor('grey6'), dlsColor('white')),
    overflowX       : 'hidden',
    overflowY       : 'auto'
  },
  actions : {
    justifyContent  : 'flex-end',
    padding         : '0px 18px 0px !important',
    backgroundColor : toggleThemeStyle(dlsColor('grey6'), dlsColor('white')),
    boxShadow       : dlsShadow(5.3),
    height          : toggleThemeStyle(72, 48)
  },
  button : {
    width : toggleThemeStyle('160px', '100%')
  },
  fullscreenColor : {
    backgroundColor : 'rgba(31%, 31%, 31%, 0.8)',
    overflow        : 'hidden'
  },
  fullScreenTitle : {
    justifyContent : 'flex-start',
    paddingLeft    : '8px'
  },
  contentWrapper : {
    [theme.breakpoints.up('sm')] : {
      padding : '0px 24px 12px'
    }
  },
  title : {
    display        : 'flex',
    justifyContent : 'space-between',
    margin         : '12px auto'
  },
  left : {
    textAlign : 'left'
  },
  right : {
    textAlign : 'right'
  },
  authorInfo : {
    overflowWrap  : 'anywhere',
    fontSize      : '16px',
    lineHeight    : '24px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey1')
  },
  timeInfo : {
    ...dlsTypography('videoAddress'),
    color      : dlsColor('grey2'),
    fontSize   : '14px',
    lineHeight : '20px'
  },
  videoContainer : {
    paddingBottom : '15px'
  },
  // the current theme's details lines, as its other dialogs set them - the
  // asset and event names as uppercase field labels (Roboto, the app's
  // Archivo Narrow), the operator and date as body text (Inter) ...
  currentAuthorInfo : {
    fontFamily : 'Roboto',
    fontWeight : 700,
    color      : dlsColor('grey2')
  },
  currentTimeInfo : {
    fontFamily    : 'Inter',
    fontWeight    : 400,
    letterSpacing : '0px'
  },
  currentCloseButton : {
    position : 'absolute',
    right    : '19px'
  },
  primaryButton : toggleThemeStyle({
    width                          : '191px',
    marginRight                    : '0px',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  }, {
    width               : '68px',
    marginRight         : '28px',
    '& .MuiButton-root' : {
      fontWeight : '500 !important',
      height     : '29px',
      fontFamily : 'Inter',
      fontSize   : '14px'
    },
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  })
}));

const DialogTitle = withStyles(theme => ({
  root : {
    padding : '0px !important'
  }
}))(MuiDialogTitle);

const DialogContent = withStyles(theme => ({
  dividers : {
    padding : '0px !important'
  }
}))(MuiDialogContent);

/**
 * @IonVisionDialog
 *
 * Defines the IonVisionDialog component.In this component we are calling the Dialog box to display
 * video player, controls. so that user can view the video recordings of asset.
 *
 * Figma link to refer Asset Dialog component:
 * https://www.figma.com/file/qla3Fz0FXPJEky5P31CXe2/03_Design-Sprint-3?node-id=4527%3A267981
 *
 * ```typescript
 * import IonVisionDialog from '@ion/dls/src/components/vision/IonVisionDialog';
 *
 *   const [iOnVisionURL, setIONVisionURL] = useState(null);
 *   {iOnVisionURL &&
 *      <IonVisionDialog urls={[iOnVisionURL]}
 *         setVideoOpen={setIONVisionURL}
 *         videoOpen
 *         showVideoPlayer />}
 *
 * ```
 */
export const IonVisionDialog = ({
  videoOpen,
  setVideoOpen,
  urls,
  showVideoPlayer,
  eventData,
  selectedAsset,
  elementId,
  audio = false,
  renderPlayerOnly = false
}: IonVisionDialogProps) => {
  const {isCurrent: isCurrentTheme, isLegacy: isLegacyTheme} = useThemeContext();
  const {isPhone}                                             = useMedia();
  const elementWidth                                          = isPhone ? 300 : '100vh';
  const [fullScreen, setFullScreen]                           = useState(false);

  const classes = useStyles({fullScreen, isPhone, elementWidth, isCurrentTheme});
  const authorInfoClassName = isCurrentTheme ? `${classes.authorInfo} ${classes.currentAuthorInfo}` : classes.authorInfo;
  const timeInfoClassName   = isCurrentTheme ? `${classes.timeInfo} ${classes.currentTimeInfo}` : classes.timeInfo;
  const eventDateTime = selectedAsset ? eventData?.avlEventTime : eventData?.eventTime;
  const assetName = selectedAsset ? selectedAsset.assetName : eventData?.assetName;
  const operatorNames = selectedAsset ? selectedAsset.operatorNames : eventData?.operatorNames;
  const operator = operatorNames ? operatorNames.replace(',', ', ') : null;

  function handleClose() {
    setVideoOpen(null);
  };

  return (
    <>
      <Dialog maxWidth={'md'}
        data-analytics-id={elementId}
        onClose={handleClose}
        open={videoOpen}
        classes={{
          paper       : classes.paper,
          scrollPaper : classes.scrollPaper
        }}
        fullWidth>
        <DialogTitle id='asset-video'
          onClose={handleClose}
          className={classes.dialogbg}>
          <div className={classes.controlsRight}>
            <Render.If condition={isLegacyTheme}>
              <div onClick={handleClose} className={classes.closeButton}>
                <MaterialExitCloseIcon size={IconSize.medium}
                  color={fullScreen ? dlsColor('white') : dlsColor('grey3')} />
              </div>
            </Render.If>
            <div className={classes.titleContent}>
              <span className={fullScreen ? classes.descHide : classes.titleStyle}>
                {translate('maps:popup.vision')}
              </span>
            </div>
            <Render.If condition={isCurrentTheme}>
              <div className={classes.currentCloseButton}>
                <CloseButton onClick={handleClose} />
              </div>
            </Render.If>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogbg}>
          <div className={classes.contentWrapper}>
            <div className={classes.title}>
              {!renderPlayerOnly && (
                <>
                  <div className={classes.left}>
                    <div className={authorInfoClassName}>
                      {assetName}
                    </div>
                    <div className={timeInfoClassName} style={{textTransform: 'capitalize'}}>
                      {operator}
                    </div>
                  </div>
                  <div className={classes.right}>
                    <span className={authorInfoClassName}>
                      {getEventName({
                        from : EventCodeValueType.eventCode,
                        val  : selectedAsset ? parseInt(eventData?.avlEventCode) : parseInt(eventData?.eventCode)
                      })}
                    </span>
                    <span className={timeInfoClassName} style={{textAlign: 'right', display: 'block'}}>
                      {getFormattedDate(eventDateTime)}
                    </span>
                  </div>
                </>
              )}
            </div>
            <VideoPlayer urls={urls}
              fullScreen={fullScreen}
              setFullScreen={setFullScreen}
              elementWidth={elementWidth}
              audio={audio}
              isFullScreen
              showCarousal />
          </div>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <div className={classes.primaryButton}>
            <PrimaryButton className={classes.button} onClick={handleClose}>
              {translate('ionvision:video.close')}
            </PrimaryButton>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IonVisionDialog;
